import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { clearExpandedState } from '@/utils/explorerStateStorage'

export interface DatabaseConnection {
  id: string
  name: string
  type: 'mysql' | 'postgresql'
  host: string
  port: number
  username: string
  password: string
  database?: string
  ssl?: boolean
  agentId?: string
  createdAt: string
  updatedAt: string
}

interface ConnectionState {
  connections: DatabaseConnection[]
  activeConnection: DatabaseConnection | null
  lastActiveConnectionId: string | null
  isConnecting: boolean
  addConnection: (connection: Omit<DatabaseConnection, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateConnection: (id: string, updates: Partial<DatabaseConnection>) => void
  deleteConnection: (id: string) => void
  setActiveConnection: (connection: DatabaseConnection | null) => void
  loadLastActiveConnection: () => void
  testConnection: (connection: Omit<DatabaseConnection, 'id' | 'createdAt' | 'updatedAt'> & { agentId: string }) => Promise<boolean>
}

export const useConnectionStore = create<ConnectionState>()(
  persist(
    (set, get) => ({
      connections: [],
      activeConnection: null,
      lastActiveConnectionId: null,
      isConnecting: false,

      addConnection: (connectionData) => {
        const newConnection: DatabaseConnection = {
          ...connectionData,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        set((state: ConnectionState) => ({
          connections: [...state.connections, newConnection]
        }))
      },

      updateConnection: (id, updates) => {
        set((state: ConnectionState) => ({
          connections: state.connections.map((conn: DatabaseConnection) =>
            conn.id === id
              ? { ...conn, ...updates, updatedAt: new Date().toISOString() }
              : conn
          )
        }))
      },

      deleteConnection: (id) => {
        // Clear expanded state for the deleted connection
        clearExpandedState(id)
        
        set((state: ConnectionState) => ({
          connections: state.connections.filter((conn: DatabaseConnection) => conn.id !== id),
          activeConnection: state.activeConnection?.id === id ? null : state.activeConnection,
          lastActiveConnectionId: state.lastActiveConnectionId === id ? null : state.lastActiveConnectionId
        }))
      },

      setActiveConnection: (connection) => {
        const state = get()
        const previousConnectionId = state.activeConnection?.id
        
        // Clear expanded state for the previous connection if switching to a different one
        if (previousConnectionId && connection?.id && previousConnectionId !== connection.id) {
          clearExpandedState(previousConnectionId)
          
          // Clear database users selection for the previous connection
          import('@/stores/databaseUsersSelectionStore').then(({ useDatabaseUsersSelectionStore }) => {
            useDatabaseUsersSelectionStore.getState().clearSelectionForConnection(previousConnectionId)
            return
          }).catch((error) => {
            console.error('Failed to clear database users selection on connection change:', error)
          })
        }
        
        set({ 
          activeConnection: connection,
          lastActiveConnectionId: connection?.id || null
        })
      },

      loadLastActiveConnection: () => {
        const state = get()
        if (state.lastActiveConnectionId) {
          // We'll need to fetch the connection data from the API since we only store the ID
          // This will be handled in the component that calls this function
        }
      },

      testConnection: async (connectionData) => {
        set({ isConnecting: true })
        try {
          // Import APIs
          const { DataSourcesService } = await import('@/services/apiClient')
          const { AgentProxyService } = await import('@/services/agentProxyService')
          
          // Create a temporary data source to test the connection
          const tempDataSource = await DataSourcesService.createDatasources({
            requestBody: {
              displayName: `test-${Date.now()}`,
              databaseType: connectionData.type === 'mysql' ? 'MySQL' : 'PostgreSQL',
              host: connectionData.host,
              port: connectionData.port,
              username: connectionData.username,
              password: connectionData.password,
              agentId: connectionData.agentId || '',
              defaultDatabase: connectionData.database || null,
              connectionParams: connectionData.ssl ? { ssl: true } : null,
            }
          })
          
          if (!tempDataSource.id) {
            console.error('Connection test failed: Failed to create temporary data source')
            set({ isConnecting: false })
            return false
          }
          
          // Test the connection using the temporary data source
          const testResult = await AgentProxyService.testConnection(tempDataSource.id)
          const isSuccessful = testResult && typeof testResult === 'object' && 'connection_successful' in testResult 
            ? Boolean(testResult.connection_successful) 
            : false
          
          // Clean up the temporary data source
          try {
            await DataSourcesService.deleteDatasources({
              datasourceId: tempDataSource.id,
              testCleanup: true
            })
          } catch (cleanupError) {
            console.warn('Failed to clean up temporary data source:', cleanupError)
          }
          
          set({ isConnecting: false })
          return isSuccessful
        } catch (error) {
          console.error('Connection test failed:', error)
          set({ isConnecting: false })
          
          // Handle specific error types for better user feedback
          if (error instanceof Error) {
            if (error.message.includes('agent') || error.message.includes('Agent')) {
              console.error('Agent-related error during connection test:', error.message)
            } else if (error.message.includes('connection') || error.message.includes('Connection')) {
              console.error('Database connection error during test:', error.message)
            } else if (error.message.includes('authentication') || error.message.includes('Authentication')) {
              console.error('Authentication error during connection test:', error.message)
            }
          }
          
          return false
        }
      },
    }),
    {
      name: 'connection-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist the lastActiveConnectionId, not the full connection data
      partialize: (state) => ({ lastActiveConnectionId: state.lastActiveConnectionId }),
    }
  )
)
